import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileType,
  HardDrive,
  Calendar,
  CalendarClock,
  Hash,
  MessageSquare,
  ExternalLink,
  Loader2,
  Sparkles,
  GitCompareArrows,
  ChevronDown,
  ChevronUp,
  FolderOpen,
  X,
} from "lucide-react";
import * as Popover from "@radix-ui/react-popover";
import { StateCard, Avatar } from "@/components/ui";
import { DatePicker } from "@/components/ui/DatePicker";
import type { DocumentView } from "./types";
import type { DocumentAiSummary, Id } from "@/packages/types";
import {
  ProcessingStepList,
  type ProcessingStep,
} from "./upload/ProcessingStepList";
import { SuggestionsAlertCard } from "./SuggestionsAlertCard";
import { useChecklistSuggestions } from "@/hooks/use-checklist-suggestions";
import { CategoryPicker } from "./CategoryPicker";

interface DocumentDetailContentProps {
  document: DocumentView;
  aiSummary?: DocumentAiSummary;
  isUploading: boolean;
  viewInNewTab: () => void;
  isDownloadLoading: boolean;
  onSwitchToSuggestionsTab?: () => void;
  onCategoryUpdate: (categoryId: Id<"documentCategories">) => void;
  onExpirationDateUpdate: (date: Date | undefined) => void;
}

function MetadataRow({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 py-2.5">
      <span className="text-muted shrink-0">{icon}</span>
      <span className="text-[13px] text-secondary w-20 shrink-0">{label}</span>
      <span className="text-[13px] text-primary flex-1 text-right">
        {children}
      </span>
    </div>
  );
}

function TruncatedText({
  text,
  maxLength = 300,
}: {
  text: string;
  maxLength?: number;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const shouldTruncate = text.length > maxLength;

  if (!shouldTruncate) {
    return <p className="text-[13px] text-secondary leading-relaxed">{text}</p>;
  }

  return (
    <div className="space-y-2">
      <p className="text-[13px] text-secondary leading-relaxed">
        {isExpanded ? text : `${text.slice(0, maxLength).trim()}...`}
      </p>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-1 text-[11px] text-amber-500 hover:text-amber-400 transition-colors"
      >
        {isExpanded ? (
          <>
            <ChevronUp size={12} />
            Show less
          </>
        ) : (
          <>
            <ChevronDown size={12} />
            Read more
          </>
        )}
      </button>
    </div>
  );
}

function AiSummaryLoading() {
  return (
    <StateCard variant="ai">
      <StateCard.Icon />
      <StateCard.Title>Generating summary…</StateCard.Title>
      <StateCard.Description>
        AI is analyzing this document to create a summary.
      </StateCard.Description>
    </StateCard>
  );
}

function AiSummaryReady({
  text,
  diffSummary,
  diffSummaryStatus,
  currentVersion,
}: {
  text: string;
  diffSummary?:
    | string
    | {
        changeStatus: "identical" | "different";
        magnitude?: "minor" | "moderate" | "major";
        businessSummary: string;
        textDiff?: {
          removed: string;
          added: string;
        };
        structuralChanges: string;
      };
  diffSummaryStatus?:
    | "pending"
    | "generating"
    | "completed"
    | "failed"
    | "retrying";
  currentVersion?: number;
}) {
  const [isDiffExpanded, setIsDiffExpanded] = useState(false);

  const showDiffSummary = diffSummaryStatus && diffSummaryStatus !== "pending";

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="rounded-xl border border-amber-500/20 bg-linear-to-br from-amber-500/6 to-transparent overflow-hidden"
    >
      <div className="px-4 py-2.5 border-b border-amber-500/10 flex items-center gap-2">
        <Sparkles size={13} className="text-amber-400" />
        <span className="text-[11px] font-medium text-amber-400/90 tracking-wide">
          AI Summary
        </span>
      </div>
      <div className="px-4 py-3.5">
        <TruncatedText text={text} />
      </div>

      {/* Version Diff Summary - Collapsible Subsection */}
      {showDiffSummary && (
        <>
          <div className="border-t border-amber-500/10" />
          <button
            type="button"
            onClick={() => setIsDiffExpanded(!isDiffExpanded)}
            className="w-full px-4 py-2.5 flex items-center gap-2 hover:bg-amber-500/5 transition-colors"
          >
            <GitCompareArrows size={13} className="text-sky-400" />
            <span className="text-[11px] font-medium text-sky-400/90 tracking-wide">
              Version Changes
            </span>
            <span className="text-[10px] text-sky-400/60 ml-auto">
              v{Math.max(1, (currentVersion ?? 2) - 1)} → v{currentVersion ?? 2}
            </span>
            <ChevronDown
              size={14}
              className={`text-sky-400/60 transition-transform ${
                isDiffExpanded ? "rotate-180" : ""
              }`}
            />
          </button>

          <AnimatePresence>
            {isDiffExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="px-4 pb-3.5">
                  {diffSummaryStatus === "generating" ||
                  diffSummaryStatus === "retrying" ? (
                    <div className="flex items-center gap-2 text-sky-400/70">
                      <Loader2 size={14} className="animate-spin" />
                      <span className="text-[13px]">
                        Analyzing changes between versions...
                      </span>
                    </div>
                  ) : diffSummaryStatus === "failed" ? (
                    <p className="text-[13px] text-red-400/80">
                      Unable to generate version comparison.
                    </p>
                  ) : diffSummary && diffSummaryStatus === "completed" ? (
                    <div className="space-y-3">
                      {typeof diffSummary === "string" ? (
                        <p className="text-[13px] text-secondary leading-relaxed whitespace-pre-line">
                          {diffSummary}
                        </p>
                      ) : diffSummary.changeStatus === "identical" ? (
                        <p className="text-[13px] text-green-400/80 font-medium">
                          No changes detected between versions.
                        </p>
                      ) : (
                        <>
                          {diffSummary.magnitude && (
                            <div className="flex items-center gap-2">
                              <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-sky-500/20 text-sky-400">
                                {diffSummary.magnitude}
                              </span>
                            </div>
                          )}
                          <p className="text-[13px] text-secondary leading-relaxed whitespace-pre-line">
                            {diffSummary.businessSummary}
                          </p>
                          {diffSummary.textDiff && (
                            <div className="space-y-2 text-[12px]">
                              {diffSummary.textDiff.removed && (
                                <div className="bg-red-500/10 border border-red-500/20 rounded p-2">
                                  <span className="text-red-400 font-medium">
                                    Removed:{" "}
                                  </span>
                                  <span className="text-secondary">
                                    "{diffSummary.textDiff.removed}"
                                  </span>
                                </div>
                              )}
                              {diffSummary.textDiff.added && (
                                <div className="bg-green-500/10 border border-green-500/20 rounded p-2">
                                  <span className="text-green-400 font-medium">
                                    Added:{" "}
                                  </span>
                                  <span className="text-secondary">
                                    "{diffSummary.textDiff.added}"
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                          {diffSummary.structuralChanges && (
                            <div className="text-[12px] text-secondary border-t border-sky-500/10 pt-2 mt-2">
                              <span className="text-sky-400 font-medium">
                                Changes:{" "}
                              </span>
                              {diffSummary.structuralChanges}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ) : null}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </motion.div>
  );
}

function DocumentProcessingProgress({
  document: doc,
}: {
  document: DocumentView;
}) {
  if (!doc.isProcessing) return null;

  const steps: ProcessingStep[] = [
    {
      label: "Summary generation",
      status: doc.processingSteps?.summary ?? "pending",
    },
    {
      label: "Checklist matching",
      status: doc.processingSteps?.checklistMatching ?? "pending",
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.2 }}
      className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <Loader2 size={14} className="text-blue-400 animate-spin" />
        <span className="text-[11px] font-medium text-blue-400/90 tracking-wide uppercase">
          Processing
        </span>
      </div>
      <ProcessingStepList steps={steps} />
    </motion.div>
  );
}

export function DocumentDetailContent({
  document,
  aiSummary,
  isUploading,
  viewInNewTab,
  isDownloadLoading,
  onSwitchToSuggestionsTab,
  onCategoryUpdate,
  onExpirationDateUpdate,
}: DocumentDetailContentProps) {
  const documentId = document.id as Id<"documents">;
  const { suggestions } = useChecklistSuggestions(documentId);

  return (
    <div className="space-y-5">
      {/* Processing progress */}
      <AnimatePresence>
        {document.isProcessing && (
          <DocumentProcessingProgress document={document} />
        )}
      </AnimatePresence>

      {/* AI Summary */}
      {aiSummary?.status === "generating" && <AiSummaryLoading />}
      {aiSummary?.status === "ready" && aiSummary.text && (
        <AiSummaryReady
          text={aiSummary.text}
          diffSummary={document.diffSummary}
          diffSummaryStatus={document.diffSummaryStatus}
          currentVersion={document.currentVersion}
        />
      )}
      {!aiSummary && document.description && (
        <p className="text-[13px] text-secondary leading-relaxed">
          {document.description}
        </p>
      )}

      {/* Checklist suggestions alert */}
      {suggestions.length > 0 && onSwitchToSuggestionsTab && (
        <SuggestionsAlertCard
          count={suggestions.length}
          onViewSuggestions={onSwitchToSuggestionsTab}
        />
      )}

      {/* Upload progress indicator */}
      {isUploading && (
        <div className="rounded-xl border border-sky-500/20 bg-sky-500/5 px-4 py-3 flex items-center gap-3">
          <Loader2 size={16} className="text-sky-400 animate-spin" />
          <span className="text-[13px] text-sky-400">
            Uploading new version…
          </span>
        </div>
      )}

      {/* Document file action and metadata */}
      <div className="rounded-xl bg-card/40 border border-border/50">
        {/* File action section */}
        <div className="p-4 border-b border-border/50">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-accent-soft flex items-center justify-center shrink-0">
              <FileType size={18} className="text-accent" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] text-primary font-medium truncate">
                {document.title}
              </p>
              <p className="text-[11px] text-muted">{document.size}</p>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                type="button"
                onClick={viewInNewTab}
                disabled={isDownloadLoading}
                className="h-8 px-3 rounded-lg flex items-center gap-1.5 text-[12px] text-secondary bg-card border border-border/50 hover:text-primary hover:bg-card-hover transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ExternalLink size={13} />
                View
              </button>
            </div>
          </div>
        </div>

        {/* Metadata section */}
        <div className="px-4 divide-y divide-border/50">
          <MetadataRow icon={<FileType size={14} />} label="Type">
            {document.type.toUpperCase()}
          </MetadataRow>
          <MetadataRow icon={<HardDrive size={14} />} label="Size">
            {document.size}
          </MetadataRow>
          <MetadataRow icon={<Calendar size={14} />} label="Updated">
            {document.updatedAt}
          </MetadataRow>
          <MetadataRow icon={<Hash size={14} />} label="Version">
            v{document.currentVersion ?? 1}
          </MetadataRow>
          <div className="flex items-center gap-3 py-2.5">
            <span className="text-muted shrink-0">
              <CalendarClock size={14} />
            </span>
            <span className="text-[13px] text-secondary w-20 shrink-0">
              Expires
            </span>
            <div className="flex-1 flex items-center justify-end gap-1">
              <DatePicker
                value={
                  document.expirationDateTimestamp
                    ? new Date(document.expirationDateTimestamp)
                    : undefined
                }
                onChange={onExpirationDateUpdate}
                minDate={new Date()}
                autoConfirm
              >
                <DatePicker.Trigger variant="ghost" className="text-[13px]" />
                <Popover.Portal>
                  <Popover.Content
                    side="bottom"
                    align="end"
                    sideOffset={4}
                    className="z-[60] rounded-[12px] border border-border bg-card shadow-lg"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                  >
                    <DatePicker.Calendar />
                  </Popover.Content>
                </Popover.Portal>
              </DatePicker>
              {document.expirationDateTimestamp && (
                <button
                  type="button"
                  onClick={() => onExpirationDateUpdate(undefined)}
                  className="p-1 rounded hover:bg-card-hover text-muted hover:text-secondary transition-colors"
                  title="Clear expiration date"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          </div>
          {document.commentCount !== undefined && (
            <MetadataRow icon={<MessageSquare size={14} />} label="Comments">
              {document.commentCount}
            </MetadataRow>
          )}
          <div className="flex items-center gap-3 py-2.5">
            <span className="text-muted shrink-0">
              <FolderOpen size={14} />
            </span>
            <span className="text-[13px] text-secondary w-20 shrink-0">
              Category
            </span>
            <div className="flex-1">
              <CategoryPicker
                selectedCategoryId={
                  document.categoryId as Id<"documentCategories"> | undefined
                }
                onSelect={onCategoryUpdate}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="py-1"></div>

      {/* Collaborators */}
      {document.collaborators && document.collaborators.length > 0 && (
        <div>
          <p className="text-[11px] uppercase text-muted font-medium tracking-wider mb-2.5">
            Collaborators
          </p>
          <div className="flex items-center">
            <div className="flex -space-x-2">
              {document.collaborators.map((collab, i) => (
                <div
                  key={collab.name}
                  className="relative"
                  style={{
                    zIndex: (document.collaborators?.length ?? 0) - i,
                  }}
                >
                  <Avatar
                    name={collab.name}
                    avatarUrl={collab.avatarUrl}
                    avatarColor={collab.avatarColor}
                    size="md"
                    ring
                  />
                </div>
              ))}
            </div>

            <span className="text-[12px] text-secondary ml-3">
              {document.collaborators
                .map((c) => c.name.split(" ")[0])
                .join(", ")}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
